import React, { useEffect } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";

import {
  AGENT_DESCRIPTION_MAX_LENGTH,
  AGENT_NAME_MAX_LENGTH,
  AGENT_NAME_MIN_LENGTH,
} from "src/agents/constants";
import {
  useCreateAgentWithInitialVersion,
  useUpdateAgent,
} from "src/agents/queries/management";
import { Agent } from "src/agents/types";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Textarea } from "src/base-components/Textarea";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";
import { getApiErrorDetail } from "src/utils/stringUtils";

type UpsertAgentModalProps = {
  open: boolean;
  onClose: () => void;
  agent?: Agent;
};

type AgentForm = {
  name: string;
  description: string;
};

export const UpsertAgentModal: React.FC<UpsertAgentModalProps> = ({
  open,
  onClose,
  agent,
}) => {
  const { mutateAsync: createAgent } = useCreateAgentWithInitialVersion();
  const { mutateAsync: updateAgent } = useUpdateAgent();
  const isEditing = !!agent;

  const form = useForm<AgentForm>({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open) return; // Prevent form reset when modal is closed so it doesn't flicker
    if (agent) {
      form.reset({
        name: agent.name,
        description: agent.description,
      });
    } else {
      form.reset({
        name: "",
        description: "",
      });
    }
  }, [agent, form, open]);

  const onSubmit = async (data: AgentForm) => {
    try {
      if (isEditing) {
        await updateAgent({
          id: agent.id,
          name: data.name,
          description: data.description,
        });
        toastActions.success({
          title: "Agent updated successfully",
        });
      } else {
        await createAgent(data);
        toastActions.success({
          title: "Agent created successfully",
        });
      }
      onClose();
    } catch (error) {
      logger.error(`Failed to ${isEditing ? "update" : "create"} agent`, error);

      if (isAxiosError(error)) {
        toastActions.failure({
          title: `Failed to ${isEditing ? "update" : "create"} agent`,
          description: getApiErrorDetail(error),
        });
        return;
      }

      toastActions.failure({
        title: `Failed to ${isEditing ? "update" : "create"} agent`,
        description: "An unknown error occurred",
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>{isEditing ? "Edit agent" : "Create agent"}</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div>
              <Label required>Agent name</Label>
              <Input
                errored={!!form.formState.errors.name}
                placeholder="Type agent name"
                fullWidth
                {...form.register("name", {
                  required: "Agent name is required",
                  minLength: {
                    value: AGENT_NAME_MIN_LENGTH,
                    message: `Agent name must be at least ${AGENT_NAME_MIN_LENGTH} characters`,
                  },
                  maxLength: {
                    value: AGENT_NAME_MAX_LENGTH,
                    message: `Agent name must be less than ${AGENT_NAME_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.name && (
                <ErrorHint>{form.formState.errors.name.message}</ErrorHint>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1">
                <Label mb="mb-0" required>
                  Description
                </Label>
                <Tooltip
                  body="Adding a description helps others understand what your agent does and how to use it."
                  placement="right-start"
                >
                  <Icon color="text-gray-500" icon={faInfoCircle} size="xs" />
                </Tooltip>
              </div>
              <Textarea
                errored={!!form.formState.errors.description}
                placeholder="Type agent description"
                fullWidth
                {...form.register("description", {
                  required: "Description is required",
                  maxLength: {
                    value: AGENT_DESCRIPTION_MAX_LENGTH,
                    message: `Description must be less than ${AGENT_DESCRIPTION_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.description && (
                <ErrorHint>
                  {form.formState.errors.description.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={form.formState.isSubmitting || !form.formState.isValid}
              htmlType="submit"
              loading={form.formState.isSubmitting}
              variant="primary"
            >
              {isEditing ? "Save" : "Create"}
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
